import { z } from "zod";
import { personNamePartSchema } from "./person-name.js";
import { organizationSettingsSchema } from "./settings.js";
import { singleLineTextSchema } from "./text.js";
// Aziende viste dal gestore della piattaforma e catalogo dei permessi (piano M1a Task 10, D4, D8, D9).
// Soltanto dati di piattaforma: mai dati del database operativo di un'azienda.
export const ORGANIZATION_STATUSES = ["provisioning", "active", "suspending", "suspended", "closed"];
/**
 * I primi pezzi degli indirizzi del programma, di oggi e delle sezioni previste: `/<slug>` apre un'azienda (decisione del
 * product owner del 3 ottobre 2026), quindi un'azienda non può chiamarsi come una pagina.
 */
export const RESERVED_ORGANIZATION_SLUGS = new Set([
    "accedi",
    "acquisto",
    "registrazione",
    "abbonamento",
    "account",
    "api",
    "assets",
    "attivita",
    "cambia-azienda",
    "cambia-vista",
    "conversazioni",
    "esci",
    "gioia365",
    "home",
    "impostazioni",
    "invito",
    "lead",
    "login",
    "opportunita",
    "reimposta-password",
    "report",
    "scegli-azienda",
    "smart-list",
    "styleguide",
    "task",
]);
/**
 * Minuscole e cifre separate da trattini singoli, 3–40 caratteri: nomina anche il database operativo ed è l'indirizzo
 * dell'azienda (`/<slug>`), quindi non è il nome di una pagina del programma.
 */
export const organizationSlugSchema = z
    .string()
    .min(3)
    .max(40)
    .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/)
    .refine((slug) => !RESERVED_ORGANIZATION_SLUGS.has(slug), "This short address is a page of the program");
const { timeZone, defaultLocale, workingCalendar } = organizationSettingsSchema.shape;
/**
 * Corpo di `POST /api/platform/organizations`. `workingCalendar` omesso vale l'orario di partenza del
 * dominio. Una ripresa della creazione (stesso `slug`, azienda ancora in `provisioning`) usa i valori già
 * registrati e ignora il resto del corpo.
 */
export const createOrganizationBodySchema = z.strictObject({
    name: singleLineTextSchema(120),
    slug: organizationSlugSchema,
    timeZone,
    defaultLocale,
    workingCalendar: workingCalendar.optional(),
    ownerEmail: z.email().max(254),
    ownerFirstName: personNamePartSchema,
    ownerLastName: personNamePartSchema,
});
/**
 * Corpo di `PATCH /api/platform/organizations/:id` (decisione del product owner del 4 ottobre 2026): il nome nuovo
 * dell'azienda. L'indirizzo breve non cambia, perché è l'indirizzo dell'azienda e il nome del suo database.
 */
export const renameOrganizationBodySchema = z.strictObject({ name: singleLineTextSchema(120) });
export const platformOrganizationSchema = z.object({
    id: z.string(),
    name: z.string(),
    slug: z.string(),
    status: z.enum(ORGANIZATION_STATUSES),
    pendingTransition: z.enum(["suspended", "closed"]).nullable(),
    /** Messaggio ripulito dell'ultimo provisioning fallito, finché l'azienda resta in `provisioning`. */
    provisioningError: z.string().nullable(),
    ownerEmail: z.string(),
    ownerFirstName: z.string(),
    ownerLastName: z.string(),
    /** Nome intero derivato da nome e cognome del proprietario. */
    ownerName: z.string(),
    activeMemberships: z.number().int().min(0),
    createdAt: z.iso.datetime(),
    statusChangedAt: z.iso.datetime(),
});
/**
 * `GET /api/platform/organizations`: le aziende e se in questo ambiente i WhatsApp automatici partono soltanto verso i numeri
 * ammessi di ogni azienda (`whatsappAllowlistRequired`: sempre fuori dalla produzione, in produzione con
 * `WHATSAPP_RECIPIENT_ALLOWLIST_REQUIRED=true`; piano M6, R2, Task 7b, questione 414): la pagina mostra «Numeri WhatsApp» soltanto allora.
 */
export const platformOrganizationsResponseSchema = z.object({ organizations: z.array(platformOrganizationSchema), whatsappAllowlistRequired: z.boolean() });
export const platformOrganizationResponseSchema = z.object({ organization: platformOrganizationSchema });
/** L'invito del proprietario protetto; `manualDelivery` soltanto quando il mailer non ha consegnato. */
export const ownerInvitationSchema = z.object({
    id: z.string(),
    email: z.string(),
    expiresAt: z.iso.datetime(),
    manualDelivery: z.object({ link: z.url() }).optional(),
});
export const createOrganizationResponseSchema = z.object({
    organization: platformOrganizationSchema,
    ownerInvitation: ownerInvitationSchema,
});
export const reconcileOrganizationResponseSchema = z.object({
    organization: platformOrganizationSchema,
    reconciliation: z.object({
        changed: z.boolean(),
        projectionRows: z.number().int().min(0),
        memberships: z.array(z.string()),
        organizationStatus: z.enum(["suspended", "closed"]).nullable(),
        flagCleared: z.boolean(),
        connectionRevoked: z.boolean(),
    }),
});
// Le chiavi del catalogo sono stringhe qui: il server le confronta con il catalogo di `@gioia365/db`.
export const permissionCatalogEntrySchema = z.object({
    key: z.string(),
    kind: z.enum(["feature", "settings_section"]),
    availableToOrganizations: z.boolean(),
});
export const permissionCatalogResponseSchema = z.object({ catalog: z.array(permissionCatalogEntrySchema) });
/** Corpo di `PATCH /api/platform/permission-catalog/:key`: spegnere una funzione la toglie ai ruoli non di sistema. */
export const updatePermissionCatalogBodySchema = z.strictObject({ availableToOrganizations: z.boolean() });
export const permissionCatalogUpdateResponseSchema = z.object({
    entry: permissionCatalogEntrySchema,
    /** Ruoli non di sistema, di tutte le aziende, da cui la funzione è stata tolta. */
    rolesUpdated: z.number().int().min(0),
});
//# sourceMappingURL=organizations.js.map